<script setup>
/**
 * FormControlUsersGroupsRoles
 * Organizes and initializes the permission management interface.
 * * Logic:
 * - Scans system components to extract unique 'rolesGroup' strings.
 * - Ensures the 'userRoles' store module is initialized for the specific role ID.
 * - Presets the role data into the local form state so changes are tracked for saving.
 */
import {computed, onMounted} from 'vue'
import {useComponentsStore} from '@/stores/components'
import {useUserRolesStore} from '@/stores/userRoles' // Adjust path to your userRoles store
import {useFormStore} from '@/stores/form'
import FormControlUsersGroupsRolesGroup from './FormControlUsersGroupsRolesGroup.vue'

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    field: Object,
})

// Pinia Stores
const componentsStore = useComponentsStore()
const userRolesStore = useUserRolesStore()
const formStore = useFormStore(props.formId)

// --- Computed ---

const components = computed(() => componentsStore.items || [])

/**
 * Extracts a unique list of Roles Groups from all registered components.
 * Uses a Set to ensure uniqueness efficiently.
 */
const rolesGroups = computed(() => {
    const groups = new Set()

    components.value.forEach((obj) => {
        if (obj.roles && obj.rolesGroup) {
            groups.add(obj.rolesGroup)
        }
    })

    return Array.from(groups)
})

// --- Lifecycle (Logic from created()) ---

onMounted(async () => {
    const roleId = formStore.id

    let privileges = roleId ? userRolesStore.privileges[roleId] : undefined

    // Initialize the role in the store if it doesn't exist
    if (privileges === undefined) {
        // Inserting a new role
        if (roleId === 0) {
            userRolesStore.initRole(roleId)
            privileges = userRolesStore.privileges[roleId]
        } else {
            // When a new role is first created, its privileges must be assigned to the store state
            await userRolesStore.setPrivileges()
        }
    }

    /**
     * Bridge the privileges into the form's local state.
     * Presetting this ensures that modifications in the child components
     * trigger the main form's dirty/modified tracking.
     */
    formStore.presetData({
        name: 'roles',
        value: privileges
    })
})
</script>

<template>
    <div>
        <div
            v-for="rolesGroup in rolesGroups"
            :key="rolesGroup"
            class="mb-4"
        >
            <div class="row">
                <label class="col-sm-3 form-label">{{ rolesGroup }}</label>
                <div class="col-sm-9 control-wrapper">
                    <FormControlUsersGroupsRolesGroup
                        :form-id="formId"
                        :roles-group="rolesGroup"
                    />
                </div>
            </div>
        </div>
    </div>
</template>